import { localizeServerMessage, t } from '../i18n';
import { getAdminAuthHeaders, handleAdminUnauthorized } from '../auth/adminSession';
// admin-client/src/api/loungeApi.js
const API_BASE = String(process.env.REACT_APP_ADMIN_API_BASE_URL ||
    process.env.REACT_APP_API_BASE ||
    (process.env.NODE_ENV === 'development' ? 'http://localhost:3100' : '')).replace(/\/$/, '');
const LOUNGE_BASE = `${API_BASE}/admin/lounge`;
const LOUNGE_CATEGORIES = [
    { value: 'en', label: 'EN · English' },
    { value: 'ko', label: 'KR · 한국어' },
    { value: 'mn', label: 'MN · Монгол' },
    { value: 'ja', label: 'JA · 日本語' },
    { value: 'zh', label: 'ZH · 简体中文' },
    { value: 'zh-tw', label: 'ZH-TW · 繁體中文' },
    { value: 'vi', label: 'VI · Tiếng Việt' },
    { value: 'th', label: 'TH · ไทย' },
    { value: 'id', label: 'ID · Bahasa Indonesia' },
    { value: 'ms', label: 'MS · Bahasa Melayu' },
    { value: 'tl', label: 'TL · Filipino' },
    { value: 'ru', label: 'RU · Русский' },
    { value: 'es', label: 'ES · Español' },
    { value: 'fr', label: 'FR · Français' },
    { value: 'de', label: 'DE · Deutsch' },
    { value: 'pt', label: 'PT · Português' },
    { value: 'it', label: 'IT · Italiano' },
    { value: 'nl', label: 'NL · Nederlands' },
    { value: 'pl', label: 'PL · Polski' },
    { value: 'uk', label: 'UK · Українська' },
    { value: 'hi', label: 'HI · हिन्दी' },
    { value: 'ar', label: 'AR · العربية' },
    { value: 'tr', label: 'TR · Türkçe' },
    { value: 'bn', label: 'BN · বাংলা' },
    { value: 'ur', label: 'UR · اردو' },
    { value: 'fa', label: 'FA · فارسی' },
];
const LOUNGE_CATEGORY_VALUES = new Set(LOUNGE_CATEGORIES.map((x) => x.value));
const LEGACY_LOUNGE_CATEGORIES = new Set(["global", "europe", "north_america", "south_america", "africa", "central_asia", "middle_east", "southeast_asia", "east_asia"]);
const DEFAULT_CATEGORIES = LOUNGE_CATEGORIES;
function normalizeLoungeCategoryValue(value, fallback = 'en') {
    const v = String(value || '').trim().toLowerCase();
    if (LEGACY_LOUNGE_CATEGORIES.has(v)) return 'en';
    return LOUNGE_CATEGORY_VALUES.has(v) ? v : fallback;
}
function normalizeLoungeCategoryOptions(value) {
    if (!Array.isArray(value) || !value.length) return DEFAULT_CATEGORIES;
    const options = value.map((item) => {
        const v = typeof item === 'string' ? item : item?.value;
        return LOUNGE_CATEGORIES.find((x) => x.value === v) || null;
    }).filter(Boolean);
    return options.length ? options : DEFAULT_CATEGORIES;
}
function getLoungeCategoryLabel(value) {
    const v = normalizeLoungeCategoryValue(value);
    return LOUNGE_CATEGORIES.find((x) => x.value === v)?.label || v;
}
function joinUrl(base, path) {
    const safePath = String(path || '');
    if (!base)
        return safePath;
    return `${String(base).replace(/\/+$/, '')}${safePath.startsWith('/') ? safePath : `/${safePath}`}`;
}
function isLocalHostName(hostname) {
    const h = String(hostname || '').toLowerCase();
    return h === 'localhost' || h === '127.0.0.1' || h === '::1';
}
function resolveUploadUrl(rawUrl) {
    const raw = String(rawUrl || '').trim();
    if (!raw)
        return '';
    if (raw.startsWith('/uploads/lounge/'))
        return joinUrl(API_BASE, raw);
    try {
        const parsed = new URL(raw);
        if (parsed.pathname.startsWith('/uploads/lounge/') && isLocalHostName(parsed.hostname) && API_BASE) {
            return joinUrl(API_BASE, `${parsed.pathname}${parsed.search || ''}`);
        }
    }
    catch (_) { }
    return raw;
}
function normalizeContentBlocks(blocks) {
    return toArray(blocks).map((block) => {
        if (!block || typeof block !== 'object')
            return block;
        if (block.type === 'image' && block.url)
            return { ...block, url: resolveUploadUrl(block.url) };
        if (block.type === 'widget' && Array.isArray(block.offers))
            return { ...block, offers: block.offers.map(o => ({...o, imageUrl: o.imageUrl ? resolveUploadUrl(o.imageUrl) : ''})) };
        return block;
    });
}
function normalizePaymentOption(raw = {}) {
    const stock = Math.max(0, Number(raw.stockQuantity ?? raw.stock_quantity ?? 0));
    const sold = Math.max(0, Number(raw.soldQuantity ?? raw.sold_quantity ?? 0));
    const remaining = Math.max(0, Number(raw.remainingQuantity ?? raw.remaining_quantity ?? stock - sold));
    return {
        optionCode: String(raw.optionCode ?? raw.option_code ?? '').trim(),
        option_code: String(raw.option_code ?? raw.optionCode ?? '').trim(),
        label: String(raw.label ?? '').trim(),
        unitAmount: Number(raw.unitAmount ?? raw.unit_amount ?? 0),
        unit_amount: Number(raw.unit_amount ?? raw.unitAmount ?? 0),
        stockQuantity: stock,
        stock_quantity: stock,
        soldQuantity: sold,
        sold_quantity: sold,
        remainingQuantity: remaining,
        remaining_quantity: remaining,
        status: String(raw.status ?? 'active'),
        sortOrder: Number(raw.sortOrder ?? raw.sort_order ?? 0),
        sort_order: Number(raw.sort_order ?? raw.sortOrder ?? 0),
    };
}
function normalizePaymentOptions(value) {
    if (!Array.isArray(value))
        return [];
    return value.map(normalizePaymentOption).filter((x) => x.optionCode || x.label || x.status === 'deleted');
}
function getAuthHeaders() {
    return getAdminAuthHeaders();
}

async function request(url, options = {}) {
    const isFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;
    const res = await fetch(url, {
        method: options.method || 'GET',
        credentials: 'include',
        headers: {
            ...getAuthHeaders(),
            ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
            ...(options.headers || {}),
        },
        body: options.body,
        signal: options.signal,
    });
    handleAdminUnauthorized(res.status, url);

    const text = await res.text();
    let data = null;
    try {
        data = text ? JSON.parse(text) : null;
    }
    catch (_) {
        data = text;
    }
    if (!res.ok || (data && typeof data === 'object' && data.ok === false)) {
        const rawMessage = data?.message || (typeof data === 'string' ? data : '') || `HTTP ${res.status}`;
        const message = localizeServerMessage(rawMessage, '라운지 요청을 처리하지 못했습니다.');
        const err = new Error(message);
        err.status = res.status;
        err.data = data;
        throw err;
    }
    return data;
}
function toArray(value) {
    if (Array.isArray(value))
        return value;
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return Array.isArray(parsed) ? parsed : [];
        }
        catch (_) {
            return [];
        }
    }
    return [];
}
export function normalizeLoungePost(raw = {}) {
    const contentJson = normalizeContentBlocks(raw.contentJson ?? raw.content_json);
    const paymentOptions = normalizePaymentOptions(raw.paymentOptions ?? raw.payment_options);
    return {
        id: String(raw.id ?? raw.loungePostId ?? raw.lounge_post_id ?? raw.postCode ?? raw.post_code ?? ''),
        postCode: String(raw.postCode ?? raw.post_code ?? ''),
        agentUserCode: String(raw.agentUserCode ?? raw.agent_user_code ?? ''),
        category: normalizeLoungeCategoryValue(raw.category),
        title: String(raw.title ?? ''),
        thumbnailUrl: resolveUploadUrl(raw.thumbnailUrl ?? raw.thumbnail_url ?? ''),
        contentJson,
        status: String(raw.status ?? 'draft'),
        uploadFee: Number(raw.uploadFee ?? raw.upload_fee ?? 60),
        isPaid: Boolean(raw.isPaid ?? raw.is_paid ?? raw.paidAt ?? raw.paid_at),
        paidAt: raw.paidAt ?? raw.paid_at ?? null,
        publishedAt: raw.publishedAt ?? raw.published_at ?? null,
        publishStartAt: raw.publishStartAt ?? raw.publish_start_at ?? null,
        publishEndAt: raw.publishEndAt ?? raw.publish_end_at ?? raw.expiredAt ?? raw.expired_at ?? null,
        expiredAt: raw.expiredAt ?? raw.expired_at ?? raw.publishEndAt ?? raw.publish_end_at ?? null,
        periodDays: Number(raw.periodDays ?? raw.period_days ?? 30),
        lastPaymentCode: raw.lastPaymentCode ?? raw.last_payment_code ?? null,
        isExpired: Boolean(raw.isExpired ?? raw.is_expired ?? false),
        paymentOptions,
        payment_options: paymentOptions,
        createdAt: raw.createdAt ?? raw.created_at ?? null,
        updatedAt: raw.updatedAt ?? raw.updated_at ?? null,
    };
}
export async function fetchAdminLoungePosts(params = {}) {
    const qs = new URLSearchParams();
    if (params.status && params.status !== 'all')
        qs.set('status', params.status);
    qs.set('category', params.category === 'all' ? 'all' : normalizeLoungeCategoryValue(params.category));
    if (params.page)
        qs.set('page', params.page);
    if (params.limit)
        qs.set('limit', params.limit);
    const url = qs.toString() ? `${LOUNGE_BASE}?${qs.toString()}` : LOUNGE_BASE;
    const data = await request(url, { signal: params.signal });
    return {
        items: Array.isArray(data?.items) ? data.items.map(normalizeLoungePost) : [],
        total: Number(data?.total || 0),
        page: Number(data?.page || 1),
        limit: Number(data?.limit || 50),
        uploadFee: Number(data?.uploadFee || 60),
        periodDays: Number(data?.periodDays || 30),
        categories: normalizeLoungeCategoryOptions(data?.categories),
    };
}
export async function createLoungePost(payload) {
    const data = await request(LOUNGE_BASE, {
        method: 'POST',
        body: JSON.stringify(payload || {}),
    });
    return {
        item: normalizeLoungePost(data?.item || data),
        payment: data?.payment || null,
        uploadFee: Number(data?.uploadFee || data?.item?.uploadFee || 60),
        periodDays: Number(data?.periodDays || data?.item?.periodDays || 30),
    };
}
export async function updateLoungePost(postCode, payload) {
    const data = await request(`${LOUNGE_BASE}/${encodeURIComponent(postCode)}`, {
        method: 'PUT',
        body: JSON.stringify(payload || {}),
    });
    return {
        item: normalizeLoungePost(data?.item || data),
        payment: data?.payment || null,
        uploadFee: Number(data?.uploadFee || data?.item?.uploadFee || 60),
        periodDays: Number(data?.periodDays || data?.item?.periodDays || 30),
    };
}
export async function deleteLoungePost(postCode) {
    const data = await request(`${LOUNGE_BASE}/${encodeURIComponent(postCode)}`, {
        method: 'DELETE',
    });
    return {
        ok: true,
        item: data?.item ? normalizeLoungePost(data.item) : null,
    };
}
export async function uploadLoungeImage(file, type = 'content') {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    const qs = new URLSearchParams();
    qs.set('type', type);
    const data = await request(`${LOUNGE_BASE}/upload-image?${qs.toString()}`, {
        method: 'POST',
        body: formData,
    });
    const item = data?.item || null;
    if (!item)
        return null;
    return {
        ...item,
        url: resolveUploadUrl(item.url || item.relativeUrl || item.publicUrl),
        publicUrl: resolveUploadUrl(item.publicUrl || item.url || item.relativeUrl),
    };
}
export { LOUNGE_CATEGORIES, DEFAULT_CATEGORIES, getLoungeCategoryLabel, normalizeLoungeCategoryOptions, normalizeLoungeCategoryValue, };

export async function fetchLoungeWidgetOrders({type='',status='',page=1}={}) {
  const qs=new URLSearchParams();if(type)qs.set('type',type);if(status)qs.set('status',status);qs.set('page',String(page));
  return request(`${LOUNGE_BASE}/widget-orders?${qs.toString()}`);
}
